import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Chip,
  Pagination,
  TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format, parseISO } from 'date-fns';
import { 
  getVendors, 
  getVendorStatements, 
  createVendorStatement, 
  updateVendorStatement, 
  deleteVendorStatement,
  getVendorBalance,
  downloadVendorStatementsLast365Days,
  apiClient
} from '../../services/productapi';

const VendorStatementPage = () => {
  // State management
  const [vendors, setVendors] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentStatement, setCurrentStatement] = useState({
    vendor: null,
    transaction_date: new Date(),
    debit: '',
    credit: '',
    narration: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [vendorBalance, setVendorBalance] = useState({ balance: 0, isDebit: false });
  const [downloadLoading, setDownloadLoading] = useState(false);
  // Data fetching
  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchVendorStatements();
  }, [selectedVendor, dateFilter, searchTerm, page, pageSize]);
  
  useEffect(() => {
    if (selectedVendor) {
      fetchVendorBalance();
    } else {
      setVendorBalance({ balance: 0, isDebit: false });
    }
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await getVendors();
      setVendors(response.data);
    } catch (err) {
      setError('Error loading vendors. Please try again.');
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorStatements = async () => {
    try {
      setLoading(true);
      
      // Create search params object
      const searchParams = {
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize
      };
      
      if (searchTerm) {
        searchParams.search = searchTerm;
      }
      
      // Add vendor filter if a vendor is selected
      if (selectedVendor) {
        searchParams.vendor = selectedVendor.id;
      }
      
      const response = await getVendorStatements(null, searchParams);
      
      // Check the structure of the response data
      console.log('Vendor statements response:', response);
      
      // Ensure we have an array to work with and extract pagination info
      let responseData = [];
      if (Array.isArray(response.data)) {
        responseData = response.data;
        setTotalItems(responseData.length); // For non-paginated responses
      } else if (response.data && Array.isArray(response.data.results)) {
        responseData = response.data.results;
        // Extract pagination metadata if available
        setTotalItems(response.data.count || responseData.length);
      }
      
      // If date filter is set, filter on client side
      // Date filtering should ideally be handled by the server, but we'll do it client-side for now
      let filteredData = responseData;
      if (dateFilter && filteredData.length > 0) {
        const filterDateStr = format(dateFilter, 'yyyy-MM-dd');
        filteredData = filteredData.filter(
          statement => statement.transaction_date && statement.transaction_date.startsWith(filterDateStr)
        );
        
        // When filtering by date, we're doing it client-side so pagination info won't be accurate
        // Consider this as the new "total" for pagination purposes
        if (dateFilter) {
          setTotalItems(filteredData.length);
        }
      }
      
      setStatements(filteredData);
    } catch (err) {
      setError('Error loading vendor statements. Please try again.');
      console.error('Error fetching vendor statements:', err);
      // Set empty array to prevent errors in rendering
      setStatements([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Form handling
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentStatement(prev => ({ ...prev, [name]: value }));
  };

  const handleVendorChange = (event, newValue) => {
    setCurrentStatement(prev => ({ ...prev, vendor: newValue?.id || null }));
  };

  const handleDateChange = (date) => {
    setCurrentStatement(prev => ({ ...prev, transaction_date: date }));
  };

  const resetForm = () => {
    setCurrentStatement({
      vendor: selectedVendor ? selectedVendor.id : null,
      transaction_date: new Date(),
      debit: '',
      credit: '',
      narration: ''
    });
    setIsEditing(false);
  };

  // CRUD operations
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Format the date for API
      const formattedData = {
        ...currentStatement,
        transaction_date: format(
          currentStatement.transaction_date instanceof Date 
            ? currentStatement.transaction_date 
            : new Date(currentStatement.transaction_date),
          'yyyy-MM-dd'
        )
      };
      
      // If both debit and credit are empty or have values, show error
      if ((formattedData.debit === '' && formattedData.credit === '') || 
          (formattedData.debit !== '' && formattedData.credit !== '')) {
        setError('Please provide either debit or credit amount, not both.');
        setLoading(false);
        return;
      }

      if (isEditing) {
        await updateVendorStatement(currentStatement.id, formattedData);
        setSuccess('Vendor statement updated successfully!');
      } else {
        await createVendorStatement(formattedData);
        setSuccess('Vendor statement created successfully!');
      }
      
      resetForm();
      setDialogOpen(false);
      setPage(0); // Reset to first page after adding or updating
      fetchVendorStatements();
      
      // Refresh the vendor balance after creating/updating a statement
      if (selectedVendor) {
        fetchVendorBalance();
      }
    } catch (err) {
      setError('Error saving vendor statement. Please try again.');
      console.error('Error saving vendor statement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (statement) => {
    setCurrentStatement({
      id: statement.id,
      vendor: statement.vendor,
      transaction_date: new Date(statement.transaction_date),
      debit: statement.debit || '',
      credit: statement.credit || '',
      narration: statement.narration
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this statement?')) return;
    
    try {
      setLoading(true);
      await deleteVendorStatement(id);
      setSuccess('Vendor statement deleted successfully!');
      
      // If we're on a page with only one item and it's not the first page, go back a page
      if (statements.length === 1 && page > 0) {
        setPage(page - 1);
      }
      
      fetchVendorStatements();
      
      // Refresh the vendor balance after deletion
      if (selectedVendor) {
        fetchVendorBalance();
      }
    } catch (err) {
      setError('Error deleting vendor statement. Please try again.');
      console.error('Error deleting vendor statement:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVendorBalance = async () => {
    if (!selectedVendor) return;
    
    try {
      const response = await getVendorBalance(selectedVendor.id);
      console.log('Vendor balance response:', response.data);
      setVendorBalance({
        balance: response.data.balance,
        isDebit: response.data.is_debit,
        totalDebit: response.data.total_debit,
        totalCredit: response.data.total_credit,
        statementCount: response.data.statement_count
      });
    } catch (err) {
      console.error('Error fetching vendor balance:', err);
      // Don't show an error to the user, just set default values
      setVendorBalance({ 
        balance: 0, 
        isDebit: false,
        totalDebit: 0,
        totalCredit: 0,
        statementCount: 0
      });
    }
  };

  const handleDownloadLast365Days = async () => {
    if (!selectedVendor) {
      setError('Please select a vendor first.');
      return;
    }

    try {
      setDownloadLoading(true);
      const response = await downloadVendorStatementsLast365Days(selectedVendor.id);

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^\"]+)"?/i);
      const filename = filenameMatch?.[1] || `vendor_${selectedVendor.id}_last_365_days.xlsx`;

      const blob = new Blob([
        response.data
      ], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Last 365 days statement downloaded successfully!');
    } catch (err) {
      console.error('Error downloading vendor statements:', err);
      setError('Unable to download file. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // We now use the vendorBalance state that gets data from the backend API
  const { balance, isDebit } = vendorBalance;

  // Render functions
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Vendor Statements
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
            options={vendors}
            getOptionLabel={(option) => option.name}
            value={selectedVendor}
            onChange={(event, newValue) => {
                setSelectedVendor(newValue);
                setPage(0); // Reset to first page when changing vendor
            }}
            renderInput={(params) => <TextField {...params} label="Select Vendor" />}
            sx={{ width: 300 }} // Set your desired width here
            />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Filter by Date"
                value={dateFilter}
                onChange={(newValue) => {
                  setDateFilter(newValue);
                  setPage(0); // Reset to first page when changing date filter
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Search Narration"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0); // Reset to first page when searching
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={() => {
                  setDateFilter(null);
                  setSearchTerm('');
                  setPage(0); // Reset to first page when clearing filters
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {selectedVendor ? `${selectedVendor.name} - Statements` : 'All Vendor Statements'}
              </Typography>
              {selectedVendor && (
                <>
                  <Typography variant="body1" color="text.secondary">
                    Current Balance: 
                    {loading ? (
                      <CircularProgress size={20} sx={{ ml: 1 }} />
                    ) : (
                      <Chip 
                        label={`${isDebit ? 'Dr' : 'Cr'} ₹${balance.toFixed(2)}`} 
                        color={isDebit ? "success" : "error"} 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </Typography>
                  {vendorBalance.totalDebit !== undefined && vendorBalance.totalCredit !== undefined && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Total Transactions: {vendorBalance.statementCount || 0} | 
                      <span style={{ color: 'green' }}> Debits: ₹{vendorBalance.totalDebit?.toFixed(2)}</span> | 
                      <span style={{ color: 'red' }}> Credits: ₹{vendorBalance.totalCredit?.toFixed(2)}</span>
                    </Typography>
                  )}
                </>
              )}
            </Box>
            <Box>
              <Button
                variant="outlined"
                sx={{ mr: 1 }}
                startIcon={downloadLoading ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                onClick={handleDownloadLast365Days}
                disabled={!selectedVendor || downloadLoading}
              >
                Download Last 365 Days
              </Button>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                Add Statement
              </Button>
            </Box>
          </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Narration</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>Debit (₹)</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>Credit (₹)</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress size={30} />
                      </TableCell>
                    </TableRow>
                  ) : !Array.isArray(statements) || statements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No statements found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    statements.map((statement) => (
                      <TableRow key={statement.id || Math.random()}>
                        <TableCell>
                          {statement.transaction_date ? 
                            format(new Date(statement.transaction_date), 'dd-MM-yyyy') : 
                            'N/A'}
                        </TableCell>
                        <TableCell>{statement.vendor_name || 'N/A'}</TableCell>
                        <TableCell>{statement.narration || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ color: statement.debit ? 'success.main' : 'text.disabled' }}>
                          {statement.debit ? `₹${parseFloat(statement.debit).toFixed(2)}` : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ color: statement.credit ? 'error.main' : 'text.disabled' }}>
                          {statement.credit ? `₹${parseFloat(statement.credit).toFixed(2)}` : ''}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(statement)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(statement.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0); // Reset to first page when changing page size
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{ mt: 2 }}
            />

        {/* Statement Form Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">
              {isEditing ? 'Edit Vendor Statement' : 'Add New Vendor Statement'}
            </Typography>
            {loading && <CircularProgress size={24} color="inherit" />}
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            <Box component="form" sx={{ mt: 1 }}>
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Transaction Details
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={vendors}
                      getOptionLabel={(option) => option.name}
                      value={vendors.find(v => v.id === currentStatement.vendor) || null}
                      onChange={handleVendorChange}
                      renderInput={(params) => <TextField {...params} label="Select Vendor" required size="small" />}
                      disabled={selectedVendor && !isEditing}
                      fullWidth
                      sx={{ width: 300 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Transaction Date"
                      value={currentStatement.transaction_date}
                      onChange={handleDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth required size="small" />}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Amount Details <Typography variant="caption" component="span">(Fill either <Typography variant="caption" component="span" color="success.main" sx={{fontWeight: 'bold'}}>Debit</Typography> or <Typography variant="caption" component="span" color="error.main" sx={{fontWeight: 'bold'}}>Credit</Typography>)</Typography>
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Debit Amount (DR)"
                      name="debit"
                      type="number"
                      value={currentStatement.debit}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      color="success"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        sx: { 
                          '& input': { color: currentStatement.debit ? 'success.main' : 'inherit' }
                        }
                      }}
                      error={currentStatement.debit !== '' && currentStatement.credit !== ''}
                      helperText={currentStatement.debit !== '' && currentStatement.credit !== '' ? 
                        "Fill either debit or credit, not both" : 
                        "Amount paid to vendor"
                      }
                      sx={{ 
                        '& .MuiInputLabel-root': { 
                          color: currentStatement.debit ? 'success.main' : (currentStatement.credit ? 'text.disabled' : 'inherit'),
                          '&.Mui-focused': { color: 'success.main' }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Credit Amount (CR)"
                      name="credit"
                      type="number"
                      value={currentStatement.credit}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      color="error"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        sx: { 
                          '& input': { color: currentStatement.credit ? 'error.main' : 'inherit' }
                        }
                      }}
                      error={currentStatement.debit !== '' && currentStatement.credit !== ''}
                      helperText={currentStatement.debit !== '' && currentStatement.credit !== '' ? 
                        "Fill either debit or credit, not both" : 
                        "Goods Received from Vendor"
                      }
                      sx={{ 
                        '& .MuiInputLabel-root': { 
                          color: currentStatement.credit ? 'error.main' : (currentStatement.debit ? 'text.disabled' : 'inherit'),
                          '&.Mui-focused': { color: 'error.main' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Description
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Narration"
                      name="narration"
                      value={currentStatement.narration}
                      onChange={handleFormChange}
                      fullWidth
                      required
                      multiline
                      rows={3}
                      placeholder="Enter detailed description of this transaction"
                      helperText="Provide a clear description of what this transaction is for"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={() => setDialogOpen(false)} 
              variant="outlined" 
              color="inherit"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {isEditing ? 'Update Statement' : 'Save Statement'}
            </Button>
          </DialogActions>
        </Dialog>



        {/* Notifications */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        
        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default VendorStatementPage;
